<template>
    <div data-test-view="reviews-cycles-edit-status">
        <div class="row">
            <div class="col-xs-6 card-title-row">
                <span>
                    <span class="headline">{{ $t(`reviews.admin.statusTitle`) }}</span>
                    <br />
                    <span v-show="reviewCycle.isLocked">
                        <span class="label label-light r-default-margin-s">{{
                            $t(`reviews.admin.labelViewOnly`)
                        }}</span>
                    </span>
                    <span class="color-grey">
                        {{ reviewCycle.name.slice(0, 27) }}
                        <span v-show="reviewCycle.name.length > 27">...</span>
                    </span>
                </span>
            </div>
            <div class="col-xs-3 card-title-row">
                <button
                    v-show="reviewCycle.status.globalStatus != 'canceled' && !reviewCycle.isLocked"
                    class="btn btn-default btn-raised btn-block"
                    type="button"
                    :disabled="updatingStatus"
                    @click="updateStatus('canceled')"
                >
                    {{ $t(`reviews.admin.btnCancelReview`) }}
                </button>
            </div>
            <div class="col-xs-3 card-title-row">
                <button
                    v-show="
                        reviewCycle.status.globalStatus != 'complete' &&
                            reviewCycle.status.globalStatus != 'canceled' &&
                            reviewCycle.status.globalStatus != 'conversationsPending' &&
                            !reviewCycle.isLocked
                    "
                    v-b-popover.hover.bottom="$t(`reviews.admin.statusProceedHelp`)"
                    class="btn btn-primary btn-raised btn-block"
                    type="button"
                    :disabled="updatingStatus"
                    @click="updateStatus(statusNext)"
                >
                    <i v-show="updatingStatus" class="fal fa-refresh fa-spin"></i>
                    <span>{{ $t(`reviews.admin.btnProceed`) }}</span>
                </button>
                <button
                    v-show="
                        reviewCycle.status.globalStatus == 'conversationsPending' &&
                            !reviewCycle.isLocked
                    "
                    v-b-popover.hover.bottom="$t(`reviews.admin.statusCloseHelp`)"
                    class="btn btn-primary btn-raised btn-block"
                    type="button"
                    :disabled="updatingStatus"
                    @click="updateStatus(statusNext)"
                >
                    {{ $t(`reviews.admin.btnCloseReview`) }}
                </button>
                <button
                    v-show="reviewCycle.status.globalStatus == 'complete' && !reviewCycle.isLocked"
                    v-b-popover.hover.bottom="$t('reviews.admin.statusReopenHelp')"
                    class="btn btn-default btn-raised btn-block"
                    type="button"
                    :disabled="updatingStatus"
                    @click="updateStatus('conversationsPending')"
                >
                    {{ $t(`reviews.admin.btnReopen`) }}
                </button>
            </div>
        </div>

        <hr />

        <div
            v-show="reviewCycle.status.globalStatus == 'new' && !reviewCycle.isLocked"
            class="panel panel-default"
        >
            <div
                class="panel-body text-center color-primary clickable"
                tabindex="0"
                @click="updateStatus('draft')"
                @keyup.enter="updateStatus('draft')"
            >
                <i class="fal fa-wrench"></i>
                <span class="l-default-margin-s"
                    ><l-text :text="$t(`reviews.admin.statusBackToDraft`)"
                /></span>
            </div>
        </div>

        <!-- Start global status buttons -->

        <l-confirmation-modal
            v-if="updateStatusCheck"
            :text="
                updateStatusNewStatus == 'canceled'
                    ? $t(`reviews.admin.statusNextCanceled`)
                    : statusNextDescription
            "
            @confirm="updateStatus(updateStatusNewStatus, true)"
            @cancel="updateStatus(updateStatusNewStatus, false)"
        />

        <!-- End global status buttons -->

        <!-- Start timeline -->

        <div v-show="reviewCycle.status.managerReviewsPlan">
            <l-progress
                size="xs"
                :progress-percent="statusProgress"
                progress-bar-class="progress-bar-primary"
                hide-popover
            />

            <l-space :size="1" />

            <div
                class="row clickable"
                tabindex="0"
                @click="goTo('timeline')"
                @keyup.enter="goTo('timeline')"
            >
                <!-- Kickoff -->

                <div class="col-md-2 explanation text-center">
                    <span :class="{ 'color-primary': mState.global > 2 }">
                        <span>{{ $t(`reviews.admin.statusKickoff`) }}</span>
                        <br />
                        <span>{{ $t(`reviews.admin.statusHappensOn`) }}</span>
                        <br />
                        <strong>{{
                            reviewCycle.status.startPlan | moment("MMM DD, YYYY HH:mm")
                        }}</strong>
                    </span>
                </div>

                <!-- Deadline for peerNominations == start of peerReviews OR start of peerApprovals -->

                <div class="col-md-2 explanation text-center">
                    <span
                        v-show="
                            reviewCycle.peerReviewsEnabled && reviewCycle.peerNominationsEnabled
                        "
                        :class="{ 'color-primary': mState.global > 3 }"
                    >
                        <span>{{ $t(`reviews.admin.statusPeerNominations`) }}</span>
                        <br />
                        <span>{{ $t(`reviews.admin.statusEndingOn`) }}</span>
                        <br />
                        <strong>{{
                            reviewCycle.status.peerNominationsPlan | moment("MMM DD, YYYY HH:mm")
                        }}</strong>
                    </span>
                </div>

                <!-- Deadline for peerApproval == start of peerReviews -->

                <div class="col-md-2 explanation text-center">
                    <span
                        v-show="reviewCycle.peerReviewsEnabled && reviewCycle.peerApprovalEnabled"
                        :class="{ 'color-primary': mState.global > 4 }"
                    >
                        <span>{{ $t(`reviews.admin.statusPeerApprovals`) }}</span>
                        <br />
                        <span>{{ $t(`reviews.admin.statusEndingOn`) }}</span>
                        <br />
                        <strong>{{
                            reviewCycle.status.peerApprovalPlan | moment("MMM DD, YYYY HH:mm")
                        }}</strong>
                    </span>
                </div>

                <!-- Deadline for peerReviews == start of manager Reviews OR process ends -->

                <div class="col-md-2 explanation text-center">
                    <span
                        v-show="
                            (reviewCycle.peerReviewsEnabled ||
                                reviewCycle.directReportReviewsEnabled ||
                                reviewCycle.selfEvaluationsEnabled) &&
                                reviewCycle.managerReviewsEnabled
                        "
                        :class="{ 'color-primary': mState.global > 5 }"
                    >
                        <span>{{ $t(`reviews.admin.statusPeerReviews`) }}</span>
                        <br />
                        <span>{{ $t(`reviews.admin.statusEndingOn`) }}</span>
                        <br />
                        <strong>{{
                            reviewCycle.status.peerReviewsPlan | moment("MMM DD, YYYY HH:mm")
                        }}</strong>
                    </span>
                </div>

                <!-- Deadline for managerReviews == start of covnersations OR process ends -->

                <div class="col-md-2 explanation text-center">
                    <span
                        v-show="
                            reviewCycle.managerReviewsEnabled &&
                                !reviewCycle.visibilityControlledByReviewee
                        "
                        :class="{ 'color-primary': mState.global > 6 }"
                    >
                        <span>{{ $t(`reviews.admin.statusManagerReviews`) }}</span>
                        <br />
                        <span>{{ $t(`reviews.admin.statusEndingOn`) }}</span>
                        <br />
                        <strong>{{
                            reviewCycle.status.managerReviewsPlan | moment("MMM DD, YYYY HH:mm")
                        }}</strong>
                    </span>
                </div>

                <!-- Deadline for managerReviews / peerReviews / conversations == process ends -->

                <div class="col-md-2 explanation text-center">
                    <span
                        v-show="!reviewCycle.managerReviewsEnabled"
                        :class="{ 'color-primary': mState.global > 5 }"
                    >
                        <span>{{ $t(`reviews.admin.statusPeerReviews`) }}</span>
                        <br />
                        <span>{{ $t(`reviews.admin.statusEndingOn`) }}</span>
                        <br />
                        <strong>{{
                            reviewCycle.status.peerReviewsPlan | moment("MMM DD, YYYY HH:mm")
                        }}</strong>
                    </span>
                    <span
                        v-show="
                            reviewCycle.managerReviewsEnabled &&
                                reviewCycle.visibilityControlledByReviewee
                        "
                        :class="{ 'color-primary': mState.global > 6 }"
                    >
                        <span>{{ $t(`reviews.admin.statusManagerReviews`) }}</span>
                        <br />
                        <span>{{ $t(`reviews.admin.statusEndingOn`) }}</span>
                        <br />
                        <strong>{{
                            reviewCycle.status.managerReviewsPlan | moment("MMM DD, YYYY HH:mm")
                        }}</strong>
                    </span>
                    <span
                        v-show="
                            reviewCycle.managerReviewsEnabled &&
                                !reviewCycle.visibilityControlledByReviewee
                        "
                        :class="{ 'color-primary': mState.global > 7 }"
                    >
                        <span>{{ $t(`reviews.admin.statusConversations`) }}</span>
                        <br />
                        <span>{{ $t(`reviews.admin.statusEndingOn`) }}</span>
                        <br />
                        <strong>{{
                            reviewCycle.status.conversationsPlan | moment("MMM DD, YYYY HH:mm")
                        }}</strong>
                    </span>
                </div>
            </div>
        </div>

        <!-- End timeline -->

        <!-- Start global status -->

        <br />

        <div class="row">
            <div class="col-md-3 text-center">
                <div class="progress progress-xs"></div>
                <h4>
                    {{
                        $t(`reviews.admin.statusParticipants`, {
                            count: reviewCycle.stats.participants,
                        })
                    }}
                </h4>
            </div>
            <div
                v-b-popover.hover.top="
                    $t(`reviews.admin.statusSummary`, {
                        started: reviewCycle.stats.selfEvaluations.started,
                        completed: reviewCycle.stats.selfEvaluations.completed,
                        total: reviewCycle.stats.selfEvaluations.total,
                    })
                "
                class="col-md-3 text-center"
            >
                <div class="progress progress-xs">
                    <div
                        v-show="
                            reviewCycle.selfEvaluationsEnabled &&
                                reviewCycle.stats.selfEvaluations.pStarted
                        "
                        class="progress-bar progress-bar-warning btn-light"
                        :style="`width:${reviewCycle.stats.selfEvaluations.pStarted}%;`"
                    ></div>
                    <div
                        v-show="
                            reviewCycle.selfEvaluationsEnabled &&
                                reviewCycle.stats.selfEvaluations.pCompleted
                        "
                        class="progress-bar progress-bar-primary"
                        :style="`width:${reviewCycle.stats.selfEvaluations.pCompleted - 3}%;`"
                    ></div>
                </div>
                <h4>{{ $t(`reviews.admin.statusSelfEvaluations`) }}</h4>
                <p
                    v-show="reviewCycle.selfEvaluationsEnabled"
                    class="explanation"
                    style="margin-top: -10px"
                >
                    {{
                        $t(`reviews.admin.statusCompleted`, {
                            percentage: reviewCycle.stats.selfEvaluations.pCompleted,
                        })
                    }}
                </p>
                <p
                    v-show="!reviewCycle.selfEvaluationsEnabled"
                    class="explanation"
                    style="margin-top: -10px"
                >
                    {{ $t(`reviews.admin.statusNotApplicable`) }}
                </p>
            </div>
            <div
                v-b-popover.hover.top="
                    $t(`reviews.admin.statusSummary360`, {
                        started: reviewCycle.stats._360Reviews.started,
                        completed: reviewCycle.stats._360Reviews.completed,
                        total: reviewCycle.stats._360Reviews.total,
                    })
                "
                class="col-md-3 text-center"
            >
                <div class="progress progress-xs">
                    <div
                        v-show="
                            reviewCycle.peerReviewsEnabled && reviewCycle.stats._360Reviews.pStarted
                        "
                        class="progress-bar progress-bar-warning btn-light"
                        :style="`width:${reviewCycle.stats._360Reviews.pStarted}%;`"
                    ></div>
                    <div
                        v-show="
                            reviewCycle.peerReviewsEnabled &&
                                reviewCycle.stats._360Reviews.pCompleted
                        "
                        class="progress-bar progress-bar-primary"
                        :style="`width:${reviewCycle.stats._360Reviews.pCompleted - 3}%;`"
                    ></div>
                </div>
                <h4>{{ $t(`reviews.admin.status360Reviews`) }}</h4>
                <p
                    v-show="
                        reviewCycle.peerReviewsEnabled || reviewCycle.directReportReviewsEnabled
                    "
                    class="explanation"
                    style="margin-top: -10px"
                >
                    {{
                        $t(`reviews.admin.statusCompleted`, {
                            percentage: reviewCycle.stats._360Reviews.pCompleted,
                        })
                    }}
                </p>
                <p
                    v-show="
                        !reviewCycle.peerReviewsEnabled && !reviewCycle.directReportReviewsEnabled
                    "
                    class="explanation"
                    style="margin-top: -10px"
                >
                    {{ $t(`reviews.admin.statusNotApplicable`) }}
                </p>
            </div>
            <div
                v-b-popover.hover.top="
                    $t(`reviews.admin.statusSummaryManager`, {
                        started: reviewCycle.stats.managerReviews.started,
                        completed: reviewCycle.stats.managerReviews.completed,
                        shared: reviewCycle.stats.managerReviews.shared,
                        signed: reviewCycle.stats.managerReviews.signed,
                        total: reviewCycle.stats.managerReviews.total,
                    })
                "
                class="col-md-3 text-center"
            >
                <div class="progress progress-xs">
                    <div
                        v-show="reviewCycle.stats.managerReviews.pStarted"
                        class="progress-bar progress-bar-warning btn-light"
                        :style="`width:${reviewCycle.stats.managerReviews.pStarted}%;`"
                    ></div>
                    <div
                        v-show="reviewCycle.stats.managerReviews.pCompleted"
                        class="progress-bar progress-bar-info"
                        :style="`width:${reviewCycle.stats.managerReviews.pCompleted - 3}%;`"
                    ></div>
                    <div
                        v-show="reviewCycle.stats.managerReviews.pShared"
                        class="progress-bar progress-bar-primary"
                        :style="`width:${reviewCycle.stats.managerReviews.pShared}%;`"
                    ></div>
                    <div
                        v-show="reviewCycle.stats.managerReviews.pSigned"
                        class="progress-bar progress-bar-primary"
                        :style="`width:${reviewCycle.stats.managerReviews.pSigned}%;`"
                    ></div>
                </div>
                <h4>{{ $t(`reviews.admin.statusManagerReviews`) }}</h4>
                <p
                    v-show="reviewCycle.managerReviewsEnabled"
                    class="explanation"
                    style="margin-top: -10px"
                >
                    {{
                        $t(`reviews.admin.statusCompleted`, {
                            percentage:
                                reviewCycle.stats.managerReviews.pCompleted +
                                reviewCycle.stats.managerReviews.pShared +
                                reviewCycle.stats.managerReviews.pSigned,
                        })
                    }}
                </p>
                <p
                    v-show="!reviewCycle.managerReviewsEnabled"
                    class="explanation"
                    style="margin-top: -10px"
                >
                    {{ $t(`reviews.admin.statusNotApplicable`) }}
                </p>
            </div>
        </div>

        <div class="row">
            <div class="col-md-12">
                <h5 class="right-line">
                    <span
                        class="color-primary clickable"
                        tabindex="0"
                        @click="participantsPaginate('previous')"
                        @keyup.enter="participantsPaginate('previous')"
                    >
                        <i class="fal fa-caret-left"></i>
                        <i class="fal fa-caret-left"></i>
                    </span>
                    <span class="l-default-margin-s r-default-margin-s">{{
                        $t(`reviews.admin.statusPage`, {
                            current: pPage + 1,
                            total: pPageTotal,
                        })
                    }}</span>
                    <span
                        class="color-primary clickable"
                        tabindex="0"
                        @click="participantsPaginate('next')"
                        @keyup.enter="participantsPaginate('next')"
                    >
                        <i class="fal fa-caret-right"></i>
                        <i class="fal fa-caret-right"></i>
                    </span>
                    <span
                        class="color-primary l-default-margin clickable"
                        tabindex="0"
                        @click="participantsExpandAll()"
                        @keyup.enter="participantsExpandAll()"
                    >
                        <i class="fal fa-search-plus"></i>
                        <span class="l-default-margin-s">{{
                            $t(`reviews.admin.statusExpandAll`)
                        }}</span>
                    </span>
                    <span
                        v-show="!reviewCycle.isLocked && reviewCycle.createdAsRole != 'employee'"
                        class="color-primary l-default-margin clickable"
                        tabindex="0"
                        @click="updateParticipantModal()"
                        @keyup.enter="updateParticipantModal()"
                    >
                        <i class="fal fa-plus"></i>
                        <span class="l-default-margin-s">{{
                            $t(`reviews.admin.btnAddParticipant`)
                        }}</span>
                    </span>
                    <span
                        class="color-primary l-default-margin clickable"
                        tabindex="0"
                        @click="exportReviewCycleAnswers()"
                        @keyup.enter="exportReviewCycleAnswers()"
                    >
                        <i class="fal fa-download"></i>
                        <span class="l-default-margin-s">{{
                            $t(`reviews.admin.statusExportAnswers`)
                        }}</span>
                    </span>
                    <span
                        class="color-primary l-default-margin clickable"
                        tabindex="0"
                        @click="exportReviewCycleScores()"
                        @keyup.enter="exportReviewCycleScores()"
                    >
                        <i class="fal fa-download"></i>
                        <span class="l-default-margin-s">{{
                            $t(`reviews.admin.statusExportScores`)
                        }}</span>
                    </span>
                </h5>
            </div>
        </div>

        <div class="row">
            <div class="col-md-3">
                <l-input-text
                    v-model="pFullname"
                    style="margin-top: 0px"
                    type="text"
                    :label="``"
                    :placeholder="$t(`reviews.admin.placeholderSearchParticipant`)"
                    optional
                    @input="onParticipantFullnameFilterInput"
                />
            </div>
            <div class="col-md-3">
                <l-input-typeahead
                    style="margin-top: 0px"
                    :label="``"
                    :placeholder="$t(`reviews.admin.placeholderSearchManager`)"
                    :data="users"
                    :on-select="
                        item => {
                            pRevieweeManager = item._id;
                            getParticipants('manager');
                        }
                    "
                    :on-input-change="typeaheadManagerOnInput"
                    display-selected-value
                />
            </div>
            <div class="col-md-3">
                <l-select
                    :value="pRevieweeTeam"
                    style="margin-top: 0px"
                    :label="$t(`reviews.admin.labelTeam`)"
                    :options="optionsRevieweeTeam"
                    @change="
                        e => {
                            pRevieweeTeam = e.target.value;
                            pPage = 0;
                            getParticipants('localTeam');
                        }
                    "
                />
            </div>
            <div class="col-md-3">
                <l-select
                    v-show="reviewCycle.selfEvaluationsEnabled"
                    :value="pStatus"
                    style="margin-top: 0px"
                    :label="$t(`reviews.admin.labelStatus`)"
                    :options="optionsStatus"
                    @change="
                        e => {
                            pStatus = e.target.value;
                            pPage = 0;
                            getParticipants('status');
                        }
                    "
                />
            </div>
        </div>

        <div
            v-show="
                reviewCycle.stats.participantsWithoutManager ||
                    reviewCycle.stats.participantsNotInvited ||
                    (reviewCycle.status.nextReminderOn &&
                        !['draft', 'new', 'complete'].includes(reviewCycle.status.globalStatus) &&
                        new Date(reviewCycle.status.nextReminderOn) > new Date())
            "
            class="panel panel-default"
        >
            <div class="panel-body">
                <span
                    v-show="reviewCycle.stats.participantsWithoutManager"
                    class="clickable r-default-margin"
                    tabindex="0"
                    @click="
                        () => {
                            pStatus = 'managerNotSet';
                            pPage = 0;
                            getParticipants();
                        }
                    "
                    @keyup.enter="
                        () => {
                            pStatus = 'managerNotSet';
                            pPage = 0;
                            getParticipants();
                        }
                    "
                >
                    <i class="fal fa-warning color-danger"></i>
                    <span class="l-default-margin-s">
                        {{
                            $t(`reviews.admin.statusWithoutManager`, {
                                c: reviewCycle.stats.participantsWithoutManager,
                            })
                        }}
                    </span>
                </span>
                <span v-show="reviewCycle.stats.participantsNotInvited" class="r-default-margin">
                    <i class="fal fa-warning color-danger"></i>
                    <span class="l-default-margin-s">
                        {{
                            $t(`reviews.admin.statusNotInvited`, {
                                c: reviewCycle.stats.participantsNotInvited,
                            })
                        }}
                    </span>
                </span>
                <span
                    v-show="
                        reviewCycle.status.nextReminderOn &&
                            new Date(reviewCycle.status.nextReminderOn) > new Date() &&
                            !reviewCycle.stats.participantsNotInvited
                    "
                    class="clickable"
                    tabindex="0"
                    @click="goTo('notifications')"
                    @keyup.enter="goTo('notifications')"
                >
                    <i class="fal fa-info"></i>
                    <span class="l-default-margin-s">{{ $t(`reviews.admin.nextReminderOn`) }}</span
                    >{{ " " }}
                    {{ reviewCycle.status.nextReminderOn | moment("MMM D") }}
                </span>
            </div>
        </div>

        <div
            v-for="participant in reviewCycle.participants"
            :key="participant._id"
            class="panel"
            :class="{
                'panel-default': !participant.showDetails,
                'panel-primary': participant.showDetails,
            }"
        >
            <div class="panel-heading">
                <div class="row">
                    <div class="col-md-4">
                        {{ participant.reviewee.fullname.slice(0, 25) }}
                        <span v-if="participant.reviewee.fullname.length > 25">...</span>
                        <span class="pull-right l-default-margin">
                            <span
                                v-show="!reviewCycle.isLocked"
                                class="clickable"
                                tabindex="0"
                                @click="updateParticipantModal(participant)"
                                @keyup.enter="updateParticipantModal(participant)"
                            >
                                <i
                                    class="fal fa-pencil"
                                    :class="{ 'color-primary': !participant.showDetails }"
                                ></i>
                                <span class="btn-fake l-default-margin-s">{{
                                    $t(`reviews.admin.btnEditParticipant`)
                                }}</span>
                            </span>
                            <span
                                class="l-default-margin clickable"
                                tabindex="0"
                                @click="participantExpand(participant)"
                                @keyup.enter="participantExpand(participant)"
                            >
                                <i
                                    class="fal fa-search-plus color-primary"
                                    :class="{ 'color-primary': !participant.showDetails }"
                                ></i>
                            </span>
                        </span>
                    </div>

                    <div
                        v-show="!reviewCycle.selfEvaluationsEnabled"
                        class="col-md-2 clickable"
                        tabindex="0"
                        @click="participantExpand(participant)"
                        @keyup.enter="participantExpand(participant)"
                    >
                        <em>{{ $t(`reviews.admin.labelNA`) }}</em>
                    </div>

                    <div
                        v-show="reviewCycle.selfEvaluationsEnabled"
                        class="col-md-2 clickable"
                        tabindex="0"
                        @click="participantExpand(participant)"
                        @keyup.enter="participantExpand(participant)"
                    >
                        <i
                            v-show="participant.stats.selfAggregateStatus == 'incomplete'"
                            class="fal fa-square"
                        ></i>
                        <i
                            v-show="participant.stats.selfAggregateStatus == 'started'"
                            class="fal fa-edit"
                        ></i>
                        <i
                            v-show="participant.stats.selfAggregateStatus == 'done'"
                            class="fal fa-check-square"
                        ></i>
                        <span class="l-default-margin-s">{{
                            $t(`reviews.admin.labelStatusSelfReview`)
                        }}</span>
                    </div>

                    <div
                        v-show="
                            !reviewCycle.peerReviewsEnabled &&
                                !reviewCycle.directReportReviewsEnabled
                        "
                        class="col-md-2 clickable"
                        tabindex="0"
                        @click="participantExpand(participant)"
                        @keyup.enter="participantExpand(participant)"
                    >
                        <em>{{ $t(`reviews.admin.labelNA`) }}</em>
                    </div>

                    <div
                        v-show="
                            reviewCycle.peerReviewsEnabled || reviewCycle.directReportReviewsEnabled
                        "
                        class="col-md-2 clickable"
                        tabindex="0"
                        @click="participantExpand(participant)"
                        @keyup.enter="participantExpand(participant)"
                    >
                        <i
                            v-show="participant.stats._360ReviewsAggregateStatus == 'done'"
                            class="fal fa-check-square"
                        ></i>
                        <i
                            v-show="participant.stats._360ReviewsAggregateStatus == 'started'"
                            class="fal fa-edit"
                        ></i>
                        <i
                            v-show="participant.stats._360ReviewsAggregateStatus == 'incomplete'"
                            class="fal fa-square"
                        ></i>
                        <span class="l-default-margin-s">{{
                            $t(`reviews.admin.labelStatus360Reviews`, {
                                complete: participant.stats._360ReviewsComplete,
                                total: participant.stats._360ReviewsTotal,
                            })
                        }}</span>
                    </div>

                    <div
                        v-show="!reviewCycle.managerReviewsEnabled"
                        class="col-md-2 clickable"
                        tabindex="0"
                        @click="participantExpand(participant)"
                        @keyup.enter="participantExpand(participant)"
                    >
                        <em>{{ $t(`reviews.admin.labelNA`) }}</em>
                    </div>

                    <div
                        v-show="reviewCycle.managerReviewsEnabled"
                        class="col-md-2 clickable"
                        tabindex="0"
                        @click="participantExpand(participant)"
                        @keyup.enter="participantExpand(participant)"
                    >
                        <i
                            v-show="participant.stats.managerAggregateStatus == 'incomplete'"
                            class="fal fa-square"
                        ></i>
                        <i
                            v-show="participant.stats.managerAggregateStatus == 'started'"
                            class="fal fa-edit"
                        ></i>
                        <i
                            v-show="participant.stats.managerAggregateStatus == 'done'"
                            class="fal fa-check-square"
                        ></i>
                        <i
                            v-show="participant.stats.managerAggregateStatus == 'shared'"
                            class="fal fa-share"
                        ></i>
                        <i
                            v-show="participant.stats.managerAggregateStatus == 'signed'"
                            class="fal fa-lock"
                        ></i>
                        <i
                            v-show="participant.stats.managerAggregateStatus == 'unassigned'"
                            class="fal fa-times"
                        ></i>
                        {{ " " }}
                        <span class="l-default-margin-s">{{
                            $t(`reviews.admin.labelStatusManagerReview`)
                        }}</span>
                    </div>

                    <div
                        v-show="reviewCycle.status.globalStatus != 'new'"
                        class="col-md-2 clickable"
                        tabindex="0"
                        @click="
                            openAssessment({
                                reviewCycleId: reviewCycle._id,
                                revieweeId: participant.reviewee && participant.reviewee.user,
                                role: 'manager',
                                view: 'overview',
                                contributorId: participant.manager && participant.manager.user,
                                viewAs: 'owner',
                            })
                        "
                        @keyup.enter="
                            openAssessment({
                                reviewCycleId: reviewCycle._id,
                                revieweeId: participant.reviewee && participant.reviewee.user,
                                role: 'manager',
                                view: 'overview',
                                contributorId: participant.manager && participant.manager.user,
                                viewAs: 'owner',
                            })
                        "
                    >
                        <span
                            v-if="participant.manager && participant.manager.averageRating"
                            v-b-popover.hover.top="$t(`reviews.admin.labelManagerRating`)"
                        >
                            <i class="fal fa-bar-chart r-default-margin-s"></i>
                            {{ participant.manager.averageRating }}/{{
                                reviewCycle.mixedScale ? 100 : reviewCycle.scale.type
                            }}
                        </span>
                        <span class="pull-right">
                            <i class="fal fa-arrow-circle-right"></i>
                        </span>
                    </div>
                </div>
            </div>

            <!-- Contributor details -->

            <div v-show="participant.showDetails" class="panel-body">
                <div v-show="reviewCycle.selfEvaluationsEnabled" class="row">
                    <div class="col-md-4">
                        <i class="fal fa-caret-right tw-mr-2"></i>
                        {{ participant.reviewee.fullname }}
                    </div>
                    <div class="col-md-3">
                        {{ $t(`reviews.admin.labelStatusSelfReview`) }}
                    </div>
                    <div class="col-md-3">
                        <span
                            v-show="participant.stats.selfAggregateStatus == 'incomplete'"
                            class="label label-light"
                        >
                            <span>{{ $t(`reviews.admin.labelStatusNotStarted`) }}</span>
                        </span>
                        <span
                            v-show="participant.stats.selfAggregateStatus == 'started'"
                            class="label label-warning"
                        >
                            <span>{{ $t(`reviews.admin.labelStatusStarted`) }}</span>
                        </span>
                        <span
                            v-show="participant.stats.selfAggregateStatus == 'done'"
                            class="label label-primary"
                        >
                            <span>{{ $t(`reviews.admin.labelStatusComplete`) }}</span>
                        </span>
                    </div>
                    <div v-show="participant.reviewee.averageRating" class="col-md-2">
                        <i class="fal fa-bar-chart"></i> {{ " "
                        }}{{ participant.reviewee.averageRating }}/{{
                            reviewCycle.mixedScale ? 100 : reviewCycle.scale.type
                        }}
                    </div>
                </div>

                <div
                    v-for="directReport in participant.directReports"
                    v-show="reviewCycle.directReportReviewsEnabled"
                    :key="directReport._id"
                    class="row"
                >
                    <div class="col-md-4">
                        <i class="fal fa-caret-right tw-mr-2"></i>
                        {{ directReport.fullname }}
                    </div>
                    <div class="col-md-3">
                        {{ $t(`reviews.admin.labelStatusDirectReportReview`) }}
                    </div>
                    <div class="col-md-3">
                        <span v-show="directReport.status == 'pending'" class="label label-light">
                            <span>{{ $t(`reviews.admin.labelStatusNotStarted`) }}</span>
                        </span>
                        <span v-show="directReport.status == 'started'" class="label label-warning">
                            <span>{{ $t(`reviews.admin.labelStatusStarted`) }}</span>
                        </span>
                        <span
                            v-show="directReport.status == 'complete'"
                            class="label label-primary"
                        >
                            <span>{{ $t(`reviews.admin.labelStatusComplete`) }}</span>
                        </span>
                    </div>
                    <div v-show="directReport.averageRating" class="col-md-2">
                        <i class="fal fa-bar-chart"></i> {{ " "
                        }}{{ directReport.averageRating }}/{{
                            reviewCycle.mixedScale ? 100 : reviewCycle.scale.type
                        }}
                    </div>
                </div>

                <div
                    v-for="peer in participant.peers"
                    v-show="reviewCycle.peerReviewsEnabled"
                    :key="peer._id"
                    class="row"
                >
                    <div class="col-md-4">
                        <i class="fal fa-caret-right tw-mr-2"></i>
                        {{ peer.fullname }}
                    </div>
                    <div class="col-md-3">{{ $t(`reviews.admin.labelStatusPeerReview`) }}</div>
                    <div class="col-md-3">
                        <span
                            v-show="participant.status == 'peerApprovalPending'"
                            class="label label-warning"
                        >
                            <span>{{ $t(`reviews.admin.labelStatusConfirmationPending`) }}</span>
                        </span>
                        <span
                            v-show="
                                participant.status != 'peerApprovalPending' &&
                                    peer.status == 'pending'
                            "
                            class="label label-light"
                        >
                            <span>{{ $t(`reviews.admin.labelStatusNotStarted`) }}</span>
                        </span>
                        <span v-show="peer.status == 'started'" class="label label-warning">
                            <span>{{ $t(`reviews.admin.labelStatusStarted`) }}</span>
                        </span>
                        <span v-show="peer.status == 'complete'" class="label label-primary">
                            <span>{{ $t(`reviews.admin.labelStatusComplete`) }}</span>
                        </span>
                        <span
                            v-show="peer.status == 'declined'"
                            v-b-popover.hover.top="peer.peerDeclineMessage"
                            class="label label-warning"
                        >
                            <span>{{ $t(`reviews.admin.labelStatusDeclined`) }}</span> {{ " "
                            }}<i class="fal fa-comment"></i>
                        </span>
                    </div>
                    <div v-show="peer.averageRating" class="col-md-2">
                        <i class="fal fa-bar-chart"></i> {{ " " }}{{ peer.averageRating }}/{{
                            reviewCycle.mixedScale ? 100 : reviewCycle.scale.type
                        }}
                    </div>
                </div>

                <div
                    v-for="manager in participant.additionalManagers"
                    v-show="reviewCycle.managerReviewsEnabled"
                    :key="manager._id"
                    class="row"
                >
                    <div class="col-md-4">
                        <i class="fal fa-caret-right tw-mr-2"></i>
                        {{ manager.fullname }}
                    </div>
                    <div class="col-md-3">
                        {{ $t(`reviews.admin.labelStatusAdditionalManagerReview`) }}
                    </div>
                    <div class="col-md-3">
                        <span v-show="manager.status == 'pending'" class="label label-light">
                            <span>{{ $t(`reviews.admin.labelStatusNotStarted`) }}</span>
                        </span>
                        <span v-show="manager.status == 'started'" class="label label-warning">
                            <span>{{ $t(`reviews.admin.labelStatusStarted`) }}</span>
                        </span>
                        <span v-show="manager.status == 'complete'" class="label label-primary">
                            <span>{{ $t(`reviews.admin.labelStatusComplete`) }}</span>
                        </span>
                    </div>
                    <div v-show="manager.averageRating" class="col-md-2">
                        <i class="fal fa-bar-chart"></i> {{ " " }}{{ manager.averageRating }}/{{
                            reviewCycle.scale.type
                        }}
                    </div>
                </div>

                <div
                    v-for="manager in participant.managers"
                    v-show="reviewCycle.managerReviewsEnabled"
                    :key="manager._id"
                    class="row"
                >
                    <div class="col-md-4">
                        <i class="fal fa-caret-right tw-mr-2"></i>
                        {{ manager.fullname }}
                    </div>
                    <div class="col-md-3">
                        {{ $t(`reviews.admin.labelStatusManagerReview`) }}
                    </div>
                    <div class="col-md-3">
                        <span
                            v-show="participant.stats.managerAggregateStatus == 'incomplete'"
                            class="label label-light"
                        >
                            <span>{{ $t(`reviews.admin.labelStatusNotStarted`) }}</span>
                        </span>
                        <span
                            v-show="participant.stats.managerAggregateStatus == 'started'"
                            class="label label-warning"
                        >
                            <span>{{ $t(`reviews.admin.labelStatusStarted`) }}</span>
                        </span>
                        <span
                            v-show="participant.stats.managerAggregateStatus == 'done'"
                            class="label label-primary"
                        >
                            <span>{{ $t(`reviews.admin.labelStatusUnshared`) }}</span>
                        </span>
                        <span
                            v-show="participant.stats.managerAggregateStatus == 'shared'"
                            class="label label-primary"
                        >
                            <span>{{ $t(`reviews.admin.labelStatusShared`) }}</span>
                        </span>
                        <span
                            v-show="participant.stats.managerAggregateStatus == 'signed'"
                            class="label label-primary"
                        >
                            <span>{{ $t(`reviews.admin.labelStatusSigned`) }}</span>
                        </span>
                    </div>
                    <div v-show="manager.averageRating" class="col-md-2">
                        <i class="fal fa-bar-chart"></i> {{ " " }}{{ manager.averageRating }}/{{
                            reviewCycle.mixedScale ? 100 : reviewCycle.scale.type
                        }}
                    </div>
                </div>
            </div>
        </div>

        <!-- End Tab -->

        <!-- MODAL -->
        <l-modal v-show="showModal" size="m" close-on-backdrop-click @close="goBack">
            <template #header>{{ modalTitle }}</template>
            <template #body>
                <router-view></router-view>
            </template>
        </l-modal>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import debounce from "lodash.debounce";

import FileSaver from "file-saver";
import api from "@/api";

import format from "@/utils/format-typeahead";

import LInputText from "@/components/LInputText";
import LInputTypeahead from "@/components/LInputTypeahead";
import LModal from "@/components/LModal";
import LProgress from "@/components/LProgress";
import LSelect from "@/components/LSelect";
import LSpace from "@/components/LSpace";
import LText from "@/components/LText";
import LConfirmationModal from "@/components/LConfirmationModal";

export default {
    components: {
        LInputText,
        LInputTypeahead,
        LModal,
        LProgress,
        LSelect,
        LSpace,
        LText,
        LConfirmationModal,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            reviewCycle: {
                name: "",
                status: {},
                stats: {},
            },

            updatingStatus: false,
            statusProgress: 0,
            statusNext: "",
            statusNextDescription: "",
            updateStatusCheck: false,
            updateStatusNewStatus: "",

            pPage: 0,
            pPageSize: 20,

            pFullname: "",
            pRevieweeTeam: "all",
            pRevieweeStatus: "all",
            pStatus: "all",
            pStatusValues: [
                "all",
                "revieweeNotStarted",
                "revieweePending",
                "revieweeComplete",
                "revieweeSigned",
                "managerNotSet",
                "managerNotStarted",
                "managerPending",
                "managerComplete",
                "managerShared",
                "managerSigned",
                "peerNominationsPending",
                "peerApprovalPending",
                "peerNotStarted",
                "peerPending",
                "peerComplete",
                "directReportNotStarted",
                "directReportPending",
                "directReportComplete",
                "managerAndRevieweeSigned",
            ],

            pRevieweeManager: null,
            pRevieweeManagerName: "",

            showModal: false,
            modalTitle: "",
        };
    },
    computed: {
        ...mapGetters({
            getCycleDetails: "reviews/cycles/details",
            participantList: "reviews/participants/list",
            localTeams: "teams/local/listSortedByName",

            usersSearchList: "users/search/list",
        }),

        mState() {
            // mapped

            var mGlobal = 0;
            var mContributor = 0;

            // unmapped

            var umGlobal = this.reviewCycle.status ? this.reviewCycle.status.globalStatus : "none";
            var umContributor = this.reviewCycle.contributorDetails
                ? this.reviewCycle.contributorDetails.status
                : "none";

            // visibility

            var umVisibilityC = this.reviewCycle.contributorDetails
                ? this.reviewCycle.contributorDetails.visibility
                : "none";

            // let's map

            if (umGlobal === "canceled") mGlobal = 0;
            else if (umGlobal === "draft") mGlobal = 1;
            else if (umGlobal === "new") mGlobal = 2;
            else if (umGlobal === "peerNominationsPending") mGlobal = 3;
            else if (umGlobal === "peerApprovalPending") mGlobal = 4;
            else if (umGlobal === "peerReviewsPending") mGlobal = 5;
            else if (umGlobal === "managerReviewsPending") mGlobal = 6;
            else if (umGlobal === "conversationsPending") mGlobal = 7;
            else if (umGlobal === "complete") mGlobal = 8;

            if (umContributor === "pending") mContributor = 1;
            else if (umContributor === "complete" && umVisibilityC === "private") mContributor = 2;
            else if (umContributor === "complete") mContributor = 3;

            return {
                global: mGlobal,
                contributor: mContributor,
            };
        },

        pPageTotal() {
            return Math.ceil(
                (this.reviewCycle.stats ? this.reviewCycle.stats.participants : 0) / this.pPageSize,
            );
        },

        optionsRevieweeTeam() {
            return [
                { value: "all", text: this.$t(`reviews.admin.optionShowAll`) },
                ...this.localTeams.map(localTeam => ({
                    value: localTeam._id,
                    text: localTeam.name,
                })),
            ];
        },
        optionsStatus() {
            return this.pStatusValues.map(value => ({
                value,
                text: this.$t(`reviews.admin.${value}`),
            }));
        },

        users() {
            return this.usersSearchList.map(format.users);
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler(newRoute) {
                this.reviewCycle = this.getCycleDetails(this.id);

                this.getParticipants();
                this.updateStatusDescription();

                this.showModal = newRoute.meta && newRoute.meta.modal;
                this.modalTitle = this.getModalTitleFromRoute(newRoute);
            },
        },
    },
    methods: {
        goTo(page) {
            this.$router.push(`/reviews/cycles/edit/${this.id}/${page}`);
        },

        updateStatusDescription() {
            const globalStatus = this.reviewCycle.status
                ? this.reviewCycle.status.globalStatus
                : "new";

            const selfEvaluationsEnabled = this.reviewCycle.selfEvaluationsEnabled;
            const peerReviewsEnabled = this.reviewCycle.peerReviewsEnabled;
            const peerNominationsEnabled = this.reviewCycle.peerNominationsEnabled;
            const peerApprovalEnabled = this.reviewCycle.peerApprovalEnabled;
            const managerReviewsEnabled = this.reviewCycle.managerReviewsEnabled;
            const directReportReviewsEnabled = this.reviewCycle.directReportReviewsEnabled;
            const conversationsEnabled = this.reviewCycle.conversationsEnabled;

            if (globalStatus === "draft") {
                this.statusProgress = 1;
                this.statusNext = "new";
                this.statusNextDescription = this.$t("reviews.admin.statusNextDraft");
            } else if (globalStatus === "new" && peerReviewsEnabled && peerNominationsEnabled) {
                this.statusProgress = 5;
                this.statusNext = "peerNominationsPending";
                this.statusNextDescription = this.$t("reviews.admin.statusNextNew");
            } else if (globalStatus === "new" && peerReviewsEnabled && peerApprovalEnabled) {
                this.statusProgress = 5;
                this.statusNext = "peerApprovalPending";
                this.statusNextDescription = this.$t("reviews.admin.statusNextNew");
            } else if (
                globalStatus === "new" &&
                (selfEvaluationsEnabled || peerReviewsEnabled || directReportReviewsEnabled)
            ) {
                this.statusProgress = 5;
                this.statusNext = "peerReviewsPending";
                this.statusNextDescription = this.$t("reviews.admin.statusNextNew");
            } else if (globalStatus === "new") {
                this.statusProgress = 5;
                this.statusNext = "managerReviewsPending";
                this.statusNextDescription = this.$t("reviews.admin.statusNextNew");
            } else if (globalStatus === "peerNominationsPending" && peerApprovalEnabled) {
                this.statusProgress = 15;
                this.statusNext = "peerApprovalPending";
                this.statusNextDescription = this.$t("reviews.admin.statusNextPeerApprovals");
            } else if (globalStatus === "peerNominationsPending" && !peerApprovalEnabled) {
                this.statusProgress = 15;
                this.statusNext = "peerReviewsPending";
                this.statusNextDescription = this.$t("reviews.admin.statusNextPeerReviews");
            } else if (globalStatus === "peerApprovalPending") {
                this.statusProgress = 30;
                this.statusNext = "peerReviewsPending";
                this.statusNextDescription = this.$t("reviews.admin.statusNextPeerReviews");
            } else if (globalStatus === "peerReviewsPending" && managerReviewsEnabled) {
                this.statusProgress = 50;
                this.statusNext = "managerReviewsPending";
                this.statusNextDescription = this.$t("reviews.admin.statusNextManagerReviews");
            } else if (globalStatus === "peerReviewsPending" && !managerReviewsEnabled) {
                this.statusProgress = 50;
                this.statusNext = "complete";
                this.statusNextDescription = this.$t("reviews.admin.statusNextCompleteNoTalks");
            } else if (globalStatus === "managerReviewsPending" && !conversationsEnabled) {
                this.statusProgress = 70;
                this.statusNext = "complete";
                this.statusNextDescription = this.$t("reviews.admin.statusNextConversations");
            } else if (globalStatus === "managerReviewsPending" && conversationsEnabled) {
                this.statusProgress = 70;
                this.statusNext = "conversationsPending";
                this.statusNextDescription = this.$t("reviews.admin.statusNextComplete");
            } else if (globalStatus === "conversationsPending") {
                this.statusProgress = 90;
                this.statusNext = "complete";
                this.statusNextDescription = this.$t("reviews.admin.statusNextComplete");
            } else if (globalStatus === "complete") {
                this.statusProgress = 100;
            } else if (globalStatus === "canceled") {
                this.statusProgress = 100;
            }
        },

        async updateStatus(newStatus, confirmed) {
            this.updateStatusNewStatus = newStatus;

            if (typeof confirmed === "undefined" && newStatus != "draft") {
                this.updateStatusCheck = true;
                return;
            } else if (!confirmed && newStatus != "draft") {
                this.updateStatusCheck = false;
                return;
            } else {
                this.updateStatusCheck = false;
            }

            this.updatingStatus = true;

            try {
                await this.$store.dispatch({
                    type: "reviews/cycles/updateStatus",
                    payload: { id: this.id, status: newStatus },
                });

                if (this.updateStatusNewStatus === "draft") {
                    this.$store.dispatch({
                        type: "reviews/assessments/getList",
                        payload: { scope: "admin" },
                    });
                    this.$router.push(`/reviews/cycles/edit/${this.id}`);
                } else if (this.updateStatusNewStatus === "canceled") {
                    this.$store.dispatch({
                        type: "reviews/assessments/getList",
                        payload: { scope: "admin" },
                    });
                    this.$router.push(`/reviews/cycles`);
                } else {
                    this.reviewCycle = {
                        ...this.reviewCycle,
                        ...this.getCycleDetails(this.id),
                    };

                    this.updateStatusDescription();
                }
            } finally {
                this.updatingStatus = false;
            }
        },

        onParticipantFullnameFilterInput: debounce(function() {
            this.pPage = 0;
            this.getParticipants("reviewee");
        }, 300),

        participantsPaginate(direction) {
            if (direction === "next" && this.pPage < this.pPageTotal - 1) {
                this.pPage += 1;
            } else if (direction === "previous" && this.pPage > 0) {
                this.pPage -= 1;
            }

            this.getParticipants();
        },

        async getParticipants(scope) {
            if (scope !== "manager") {
                this.pRevieweeManager = null;
            } else {
                this.pRevieweeManagerName = "";
            }

            if (scope) {
                this.pPage = 0;
            }

            await this.$store.dispatch({
                type: "reviews/participants/getList",
                payload: {
                    id: this.id,
                    fullname: this.pFullname,
                    localTeam: this.pRevieweeTeam,
                    manager: this.pRevieweeManager,
                    status: this.pStatus,
                    page: this.pPage,
                },
            });

            this.$set(this.reviewCycle, "participants", Array.from(this.participantList));
        },
        participantExpand(participant) {
            this.$set(participant, "showDetails", !participant.showDetails);
        },
        participantsExpandAll() {
            this.reviewCycle.participants.forEach((_, index) => {
                this.$set(this.reviewCycle.participants[index], "showDetails", true);
            });
        },

        updateParticipantModal(participant) {
            if (participant?._id) {
                this.$router.push(
                    `/reviews/cycles/edit/${this.id}/status/participant/edit/${participant._id}`,
                );
            } else {
                this.$router.push(`/reviews/cycles/edit/${this.id}/status/participant/add`);
            }
        },

        exportReviewCycleAnswers() {
            return api
                .get("/api/reviewcycles/get/details/" + this.id + "/export/answers", {
                    responseType: "arraybuffer",
                })
                .then(function(response) {
                    var date = new Date();
                    var filename =
                        "Review-Export-Answers-" + date.toLocaleDateString("de") + ".xlsx";

                    FileSaver.saveAs(
                        new Blob([response.data], {
                            type:
                                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                        }),
                        filename,
                    );
                });
        },

        exportReviewCycleScores() {
            return api
                .get("/api/reviewcycles/get/details/" + this.id + "/export/scores", {
                    responseType: "arraybuffer",
                })
                .then(function(response) {
                    var date = new Date();
                    var filename =
                        "Review-Export-Scores-" + date.toLocaleDateString("de") + ".xlsx";

                    FileSaver.saveAs(
                        new Blob([response.data], {
                            type:
                                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                        }),
                        filename,
                    );
                });
        },

        typeaheadManagerOnInput(query) {
            if (query.length >= 2) {
                this.$store.dispatch({ type: "users/search/findByName", query });
            } else {
                this.$store.dispatch({ type: "users/search/reset" });
            }
        },

        openAssessment(ctx) {
            this.$router.push(
                "/reviews/update/" +
                    ctx.reviewCycleId +
                    "/reviewee/" +
                    ctx.revieweeId +
                    "/manager/" +
                    ctx.contributorId +
                    "/viewAs/owner/overview/toDo",
            );
        },

        getModalTitleFromRoute(route) {
            switch (route.name) {
                case "ReviewsCyclesEditStatusParticipantAdd":
                    return this.$t("reviews.updateParticipant.titleAddParticipant");
                case "ReviewsCyclesEditStatusParticipantEdit":
                    return this.$t("reviews.updateParticipant.titleEditParticipant");
                default:
                    return "";
            }
        },

        goBack() {
            this.$router.push(`/reviews/cycles/edit/${this.id}/status`);
        },
    },
};
</script>
